import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  copyProjectLimits,
  copyProjectRoom,
  HotelLimits,
  HotelRoom,
} from '@/features/projects/projectsSlice';
import {
  getField,
  screentimeColourField,
  screentimeDetailsField,
  screentimeFromField,
  screentimeLimitFields,
  screentimeMessageField,
  screentimePeriodField,
  screentimeQuotaField,
  screentimeTitleField,
  screentimeToField,
  setField,
  UrlDisabled,
  UrlInfo,
} from '@/features/channels/channelsSlice';
import {
  hasOwnScreentimeLimit,
  hasValidWindow,
  isHalfWindow,
  isScreenTimeConfigured,
  normalizeScreentimePeriod,
  parseHHMM,
  parseQuotaMinutes,
  periodMinutes,
  resolveScreenTime,
} from '@/features/screentime/screenTimeApi';

import Button from '@/components/Common/Button';
import Dropdown, { FieldInfo } from '@/components/Common/Dropdown';
import FlexRow from '@/components/Common/FlexRow';
import Input from '@/components/Common/Input';

import BlockedScreenSettings from './BlockedScreenSettings';
import { CatHeader } from './Project';

export interface ScreenTimeSettingsProps {
  limits:				HotelLimits | null;
  setLimits:			(lim: HotelLimits) => any;
  // Room level. Omit both for the project defaults.
  room?:				HotelRoom;
  setRoom?:				(room: HotelRoom) => any;
  isLoaded?:			boolean;
  isReadOnly?:			boolean;
}

const Hint = (props: {children?: React.ReactNode}) => (
  <span className="text-[--Dark-Grey] text-[12px] leading-[16px]">
    { props.children }
  </span>
);

const Warning = (props: {children?: React.ReactNode}) => (
  <FlexRow align="start"
           className="gap-[6px] max-w-[448px]">
    <span className="material-icons !text-[--Primetime-main] text-[16px] leading-[16px]">
      warning
    </span>
    <span className="text-[--Primetime-main] text-[12px] leading-[16px]">
      { props.children }
    </span>
  </FlexRow>
);

// ScreenTimeSettings edits the screen time fields at project level
// (limits.urls) or room level (room.fields) — the same override mechanism as
// network-error-message.
//
// Two halves: the LIMIT fields decide whether the TV is blocked, and they live
// here. What the blocked screen then LOOKS like is BlockedScreenSettings, which
// owns the last four keys — those are pure pass-through to the TV and no
// verdict on this side reads them.
//
// Three things this screen has to get right, each of which costs a support
// ticket otherwise:
//   * the quota is in MINUTES (the API reports seconds),
//   * a half-configured window blocks nobody AND records nothing, so one edge
//     without the other is a silent no-op, and
//   * the colour field must stay EMPTY until someone picks one — a native
//     colour input always renders something, and echoing that back would save
//     a colour nobody chose.
const ScreenTimeSettings = (props: ScreenTimeSettingsProps) => {
  const { t } = useTranslation();

  const loaded =	props.isLoaded === undefined
					?	true
					:	props.isLoaded;

  const projFields:	UrlInfo[] =	props.limits && props.limits.urls
								?	props.limits.urls
								:	[];

  const roomFields:	UrlInfo[] =	props.room && props.room.fields
								?	props.room.fields
								:	[];

  const isRoom = !!props.room;

  // No ACL term here. `enable-screentime` decides whether a MANAGER may see and
  // edit these fields — which the caller has already settled by choosing to
  // render us at all. It never applies to an admin, and it plays no part in
  // deciding which value a room ends up with.
  const readOnly = props.isReadOnly === true;

  const ownFields = isRoom ? roomFields : projFields;

  // `__disabled__` is the explicit no-override marker and is treated exactly
  // like an empty value, so it must not leak into an input box.
  const own = (field: string): string => {
    const val = getField(ownFields, field);

    return	val == UrlDisabled
			?	""
			:	val;
  };

  const quota		= own(screentimeQuotaField);
  const period		= own(screentimePeriodField);
  const from		= own(screentimeFromField);
  const to			= own(screentimeToField);
  const message		= own(screentimeMessageField);
  const details		= own(screentimeDetailsField);
  const colour		= own(screentimeColourField);
  const title		= own(screentimeTitleField);

  const projQuota	= getField(projFields, screentimeQuotaField);
  const projPeriod	= getField(projFields, screentimePeriodField);
  const projFrom	= getField(projFields, screentimeFromField);
  const projTo		= getField(projFields, screentimeToField);
  const projColour	= getField(projFields, screentimeColourField);
  const projTitle	= getField(projFields, screentimeTitleField);

  // What the device will actually see, after project -> room resolution.
  const resolved = resolveScreenTime(isRoom ? roomFields : undefined, projFields);

  const updateField = (field: string, val: string) => {
    if (readOnly)
      return;

    if (isRoom) {
      if (	!props.room ||
			!props.setRoom
		 )
        return;

      const rume = copyProjectRoom(props.room);
      rume.fields = setField(rume.fields || [], field, val);
      props.setRoom(rume);
      return;
    }

    if (!props.limits)
      return;

    const limits = copyProjectLimits(props.limits);
    limits.urls = setField(limits.urls || [], field, val);
    props.setLimits(limits);
  };

  // Minutes only. Typing "08:00" or "480s" into the allowance is the single
  // easiest way to get this feature wrong, so non-digits never reach the store.
  const updateQuota = (val: string) => {
    updateField(screentimeQuotaField, val.replace(/[^0-9]/g, ''));
  };

  // Removing the limits clears the four LIMIT keys (quota, period, from, to) in
  // ONE write and leaves the blocked screen settings alone. Doing it field by
  // field through updateField would copy from the same stale props each time
  // and only the last one would survive — and a partial clear is exactly the
  // failure this action exists to prevent: clearing only the window leaves the
  // quota still enforcing, and clearing one window edge leaves a form that
  // still looks configured while restricting nothing.
  const clearAllFields = () => {
    if (readOnly)
      return;

    const strip = (urls: UrlInfo[]) =>
      screentimeLimitFields.reduce((acc: UrlInfo[], field: string) => setField(acc, field, ""), urls || []);

    if (isRoom) {
      if (	!props.room ||
			!props.setRoom
		 )
        return;

      const rume = copyProjectRoom(props.room);
      rume.fields = strip(rume.fields);
      props.setRoom(rume);
      return;
    }

    if (!props.limits)
      return;

    const limits = copyProjectLimits(props.limits);
    limits.urls = strip(limits.urls);
    props.setLimits(limits);
  };

  // Is there anything to clear in THIS scope? `__disabled__` reads as empty.
  const hasOwnValues = screentimeLimitFields.some((field: string) => own(field) != '');

  // Describes what a room falls back to once its own keys are gone. At room
  // level an empty field means "inherit the project", not "no limit", so a room
  // whose project defines a window stays restricted after a reset.
  const describeLimit = (): string => {
    const parts: string[] = [];
    const mins = parseQuotaMinutes(resolved.quota);

    if (mins > 0) {
      parts.push(t('projects:project:screentime:limit_quota_n', {
        n:		mins,
        period:	t('projects:project:screentime:period:' + normalizeScreentimePeriod(resolved.period)).toLowerCase(),
      }));
    }

    if (hasValidWindow(resolved.from, resolved.to)) {
      parts.push(t('projects:project:screentime:limit_window_n', {
        from:	resolved.from,
        to:		resolved.to,
      }));
    }

    return parts.join(' · ');
  };

  // Gated on the LIMIT fields alone, not on hasOwnValues. A room that only
  // picked its own background colour or title still follows the project's
  // limit, and hiding this warning there would drop the one line that explains
  // why the room is restricted at all.
  const inheritsProjectLimit =	isRoom &&
								!hasOwnScreentimeLimit(ownFields) &&
								isScreenTimeConfigured(projQuota, projFrom, projTo);

  const periodOpts: FieldInfo[] = [
    // Room level can fall back to the project's period, so it gets the extra
    // "Project default" entry that writes an empty value.
    ...(isRoom
        ?	[{ value: "", desc: t('projects:project:screentime:project_default') }]
        :	[]
       ),
    { value: "day",		desc: t('projects:project:screentime:period:day') },
    { value: "week",	desc: t('projects:project:screentime:period:week') },
    { value: "month",	desc: t('projects:project:screentime:period:month') },
    { value: "year",	desc: t('projects:project:screentime:period:year') },
  ];

  const effectivePeriod = normalizeScreentimePeriod(resolved.period);

  const quotaMinutes = parseQuotaMinutes(quota);

  // The seconds-instead-of-minutes trap: 28800 does not mean 8 hours, it means
  // 20 days. Warn rather than block — a long quota is legal for week/month.
  const quotaOversized =	quotaMinutes > 0 &&
							quotaMinutes > periodMinutes(effectivePeriod);

  const halfWindow = isHalfWindow(resolved.from, resolved.to);

  const badFrom =	from != "" &&
					parseHHMM(from) === null;

  const badTo =		to != "" &&
					parseHHMM(to) === null;

  const wrapsMidnight =	hasValidWindow(resolved.from, resolved.to) &&
						(parseHHMM(resolved.to) as number) < (parseHHMM(resolved.from) as number);

  const projectDefaultHint = (value: string) =>
    isRoom && value != '' && value != UrlDisabled
    ?	<Hint>
          { t('projects:project:screentime:project_default_n', {n: value}) }
        </Hint>
    :	null;

  if (!props.limits)
    return null;

  return (
  <FlexRow direction="column"
           align="start"
           className="py-0 gap-[16px] max-w-[672px]">
    <CatHeader caption="projects:project:screentime:caption"
               margin="mt-[8px]" />

  {/* The limit fields stay in the narrow column the rest of the project forms
      use. Only the blocked screen card below breaks out of it — a colour wheel
      beside its readouts does not fit in 448px. */}
    <FlexRow direction="column"
             align="start"
             className="gap-[16px] max-w-[448px]">
    <FlexRow direction="column"
             align="start"
             className="gap-[4px]">
      <Input label={t('projects:project:screentime:quota:caption')}
             isLoaded={loaded}
             isReadOnly={readOnly}
             inputMode="numeric"
             placeholder={	isRoom && projQuota != ''
							?	projQuota
							:	'480'
						  }
             value={quota}
             onValueChange={updateQuota} />
      <Hint>
        { t('projects:project:screentime:quota:help') }
      </Hint>
      { projectDefaultHint(projQuota) }
    {quotaOversized &&
      <Warning>
        { t('projects:project:screentime:quota:oversized', {
            period:	t('projects:project:screentime:period:' + effectivePeriod),
            max:	periodMinutes(effectivePeriod),
          }) }
      </Warning>
    }
    </FlexRow>

    <FlexRow direction="column"
             align="start"
             className="gap-[4px]">
      <Dropdown label={t('projects:project:screentime:period:caption')}
                isLoaded={loaded}
                isDisabled={readOnly}
                value={	isRoom
						?	period
						:	(period || "day")
					  }
                options={periodOpts}
                setValue={(val) => updateField(screentimePeriodField, val ? val.toString() : "")}>
        <>
        </>
      </Dropdown>
      { projectDefaultHint(projPeriod) }
    </FlexRow>

    {/* labelPlacement="outside" is not cosmetic here. A type="time" input is
        never "empty" to the browser — it always paints --:-- — but HeroUI only
        lifts an INSIDE label once it thinks the field is filled, so the label
        sits opaquely on top of the clock control and the field reads as if
        there were nothing to set. Outside placement renders the label as a
        plain block above a label-less input, the same fix the Login/Mailer
        forms use. */}
    <FlexRow align="start"
             className="gap-[12px]">
      <Input label={t('projects:project:screentime:from:caption')}
             type="time"
             labelPlacement="outside"
             isLoaded={loaded}
             isReadOnly={readOnly}
             value={badFrom ? '' : from}
             onValueChange={(val: string) => updateField(screentimeFromField, val)} />
      <Input label={t('projects:project:screentime:to:caption')}
             type="time"
             labelPlacement="outside"
             isLoaded={loaded}
             isReadOnly={readOnly}
             value={badTo ? '' : to}
             onValueChange={(val: string) => updateField(screentimeToField, val)} />
    </FlexRow>
    <FlexRow direction="column"
             align="start"
             className="gap-[4px]">
      <Hint>
        { t('projects:project:screentime:window_help') }
      </Hint>
      { projectDefaultHint(projFrom && projTo ? `${projFrom} – ${projTo}` : '') }
    {(badFrom || badTo) &&
      <Warning>
        { t('projects:project:screentime:window_unparseable', {value: badFrom ? from : to}) }
      </Warning>
    }
    {halfWindow && !badFrom && !badTo &&
      <Warning>
        { t('projects:project:screentime:window_half') }
      </Warning>
    }
    {wrapsMidnight &&
      <Hint>
        { t('projects:project:screentime:window_overnight') }
      </Hint>
    }
    </FlexRow>

    {/* Removing the limits is NOT the same act as resetting usage: this clears
        the four limit settings and keeps every recorded minute, and it is reversible
        by simply setting the values again. Labelled apart so the two can't be
        confused. Takes effect on the page's own Save — these are ordinary
        settings fields and need no endpoint of their own. */}
    {!readOnly && hasOwnValues &&
      <FlexRow direction="column"
               align="start"
               className="gap-[6px]">
        <Button caption={t('projects:project:screentime:remove_limits:caption')}
                icon="layers_clear"
                layout="white"
                onClick={clearAllFields} />
        <Hint>
          { t('projects:project:screentime:remove_limits:hint') }
        </Hint>
      </FlexRow>
    }

    {inheritsProjectLimit &&
      <Warning>
        { t('projects:project:screentime:remove_limits:follows_project_n', {n: describeLimit()}) }
      </Warning>
    }
    </FlexRow>

    <CatHeader caption="projects:project:screentime:blocked_screen:section"
               margin="mt-[16px]" />

    <BlockedScreenSettings details={details}
                           colour={colour}
                           title={title}
                           message={message}
                           projColour={projColour}
                           projTitle={projTitle}
                           isRoom={isRoom}
                           isLoaded={loaded}
                           isReadOnly={readOnly}
                           updateField={updateField} />
  </FlexRow>
  );
};

export default ScreenTimeSettings;
